import React, { useEffect, useMemo, useState } from "react";
import { useStore } from "react-redux";
import { CloudUpload, CropSquare, Polyline } from "@mui/icons-material";
import { Typography } from "@mui/material";

import { AsyncOperationState } from "fond/async/redux";
import { usePolygonSelect } from "fond/project/panels/polygon/usePolygonSelect";
import { PolygonStatus } from "fond/project/polygon";
import { PolygonState, retryDownload, selectUploadOption } from "fond/project/polygon/redux";
import { closePolygonSelect, dismissUploadError, POLYGON_SELECT_UPLOAD_ID } from "fond/project/redux";
import { dismissBaseDataTask, selectBaseDataTaskRecords, startBaseDataTask } from "fond/redux/baseDataTasks";
import { Store } from "fond/types";
import { useAppDispatch } from "fond/utils/hooks";
import { useStackedNavigationContext } from "fond/widgets";

import { ADD_BOUNDARY_FIRST, AREA_DOWNLOAD_ERROR, IMPORT_SAVE_ERROR } from "../copy";
import { BASE_DATA_LAYERS, BaseDataLayerKey } from "../layers";
import { BASE_DATA_POLYGON_DRAW_SCREEN, PolygonDrawParams } from "../polygonDraw";
import { getImportTaskFor } from "../tasks";
import { useBaseDataModelContext } from "../useBaseDataModel";

import {
  BoundaryImportKey,
  getAddressLimitWarning,
  getImportAreaWarning,
  getImportCompanion,
  isBoundaryImportKey,
  isImportAreaFor,
  isReadingPolygonFile,
  POLYGON_FILE_STATES,
  sortBoundaryImportKeys,
} from "./boundary";
import { FlyoutSubOption, FlyoutSubOptions } from "./FlyoutSubOptions";
import ImportChecklist from "./ImportChecklist";
import { formatImportNouns } from "./importTask";
import PolygonFileUpload from "./PolygonFileUpload";
import ReplaceConfirmation from "./ReplaceConfirmation";
import TaskProgress, { TaskResult } from "./TaskProgress";
import { AreaImportSource, useBeginImportAreaSelect, useUploadImportArea } from "./useBoundary";
import { useImportWithinBoundary } from "./useImportWithinBoundary";

/** Lead-in of the import checklist for each kind of area. */
const CHECKLIST_LEADS = {
  boundary: "Import within the boundary:",
  drawn: "Import within the drawn area:",
  file: "Import within the uploaded area:",
} as const;

interface AreaSelectSectionProps {
  layerKey: BaseDataLayerKey;
  /** Whether the layer already has data that an import replaces. */
  hasData: boolean;
  /** Called when the user backs out of replacing the layer's data. */
  onCancel(): void;
}

interface AreaImportProps extends Omit<AreaSelectSectionProps, "layerKey"> {
  layerKey: BoundaryImportKey;
}

const AreaImport: React.FC<AreaImportProps> = ({ layerKey, hasData, onCancel }) => {
  const dispatch = useAppDispatch();
  const store = useStore<Store>();
  const { open } = useStackedNavigationContext();
  const { rows, tasks } = useBaseDataModelContext();
  const polygonSelect = usePolygonSelect();
  const beginImportAreaSelect = useBeginImportAreaSelect();
  const uploadImportArea = useUploadImportArea();
  const companion = getImportCompanion(layerKey);
  const companionHasData = !!companion && rows[companion].hasData;
  /** Whether the companion is ticked, once the user has changed it. */
  const [companionTick, setCompanionTick] = useState<boolean | null>(null);
  const isCompanionTicked = companionTick ?? !companionHasData;
  const checklistKeys = useMemo(
    () => sortBoundaryImportKeys(companion && isCompanionTicked ? [layerKey, companion] : [layerKey]),
    [companion, isCompanionTicked, layerKey]
  );
  const importWithinBoundary = useImportWithinBoundary(layerKey);
  const task = getImportTaskFor(tasks, layerKey, [layerKey]);
  const [isReplaceConfirmed, setIsReplaceConfirmed] = useState(false);
  /** The area chosen to import within, when it isn't drawn on the map. */
  const [chosenArea, setChosenArea] = useState<"boundary" | "file" | null>(null);

  const { polygon, activeLayerGroupId } = polygonSelect;
  const { name, noun, layerGroupId } = BASE_DATA_LAYERS[layerKey];
  const isAreaFromFile = chosenArea === "file";
  const isActive = isImportAreaFor(polygon, activeLayerGroupId, layerKey);
  const isAreaReady = isActive && polygon.polygonState === PolygonState.complete;
  /** Whether an area drawn on the map is waiting for the layers to import to be confirmed. */
  const isDrawnArea = !!companion && isAreaReady && !isAreaFromFile;
  const areaWarning = isAreaReady ? getImportAreaWarning(polygon, layerKey) : null;
  const isAreaValid =
    isAreaReady && polygon.downloadState === AsyncOperationState.success && polygon.status === PolygonStatus.ValidArea && !areaWarning;
  const areaSource: AreaImportSource = isAreaFromFile ? "uploadedAreaImport" : "drawnAreaImport";
  const importNouns = formatImportNouns(task ? task.keys.filter(isBoundaryImportKey) : checklistKeys);
  /** Title of a problem with the area, before the layers to import are confirmed. */
  const areaErrorTitle = `Couldn't import ${noun}`;

  // Leave polygon select when the option closes, unless an area is being drawn or imported; an uploaded area that isn't importing yet stops.
  useEffect(
    () => () => {
      const state = store.getState();
      const { project } = state;
      if (project.polygon.isOpen && project.activeLayerGroupId === layerGroupId && POLYGON_FILE_STATES.includes(project.polygon.polygonState)) {
        dispatch(closePolygonSelect());
      }
      if (isReadingPolygonFile(selectBaseDataTaskRecords(state)[layerKey], "uploadedAreaImport")) dispatch(dismissBaseDataTask(layerKey));
    },
    [dispatch, layerGroupId, layerKey, store]
  );

  // Import from an uploaded area as soon as FOND's data inside it has downloaded, unless the layers to import are confirmed first.
  useEffect(() => {
    if (!companion && isAreaFromFile && isAreaValid) uploadImportArea(layerKey, "uploadedAreaImport", [layerKey]);
  }, [companion, isAreaFromFile, isAreaValid, layerKey, uploadImportArea]);

  const importFromBoundary = () => {
    if (companion) {
      setChosenArea("boundary");
    } else {
      importWithinBoundary([layerKey]);
    }
  };

  const uploadAreaFile = () => {
    dispatch(startBaseDataTask({ key: layerKey, source: "uploadedAreaImport", keys: [layerKey], stage: "readingFile" }));
    beginImportAreaSelect(layerKey);
    dispatch(selectUploadOption());
    polygonSelect.uploadPolygon();
  };

  const cancelArea = () => {
    if (isActive) dispatch(closePolygonSelect());
    if (polygonSelect.upload) polygonSelect.dismissPolygonUploadError();
    if (isReadingPolygonFile(selectBaseDataTaskRecords(store.getState())[layerKey], "uploadedAreaImport")) dispatch(dismissBaseDataTask(layerKey));
    setChosenArea(null);
  };

  const importArea = () => uploadImportArea(layerKey, areaSource, checklistKeys);

  const retry = () => {
    if (!task) return;
    if (task.kind === "boundaryImport") {
      importWithinBoundary(task.keys);
    } else if (task.kind === "drawnAreaImport" || task.kind === "uploadedAreaImport") {
      uploadImportArea(layerKey, task.kind, task.keys.filter(isBoundaryImportKey));
    }
  };

  const dismissTask = () => {
    if (task?.kind !== "boundaryImport") {
      dispatch(closePolygonSelect());
      dispatch(dismissUploadError(POLYGON_SELECT_UPLOAD_ID));
    }
    dispatch(dismissBaseDataTask(layerKey));
  };

  const chooseAnotherFile = () => {
    dispatch(closePolygonSelect());
    dispatch(dismissBaseDataTask(layerKey));
  };

  const renderChecklist = (area: keyof typeof CHECKLIST_LEADS, companionKey: BoundaryImportKey) => {
    const isBoundary = area === "boundary";
    return (
      <ImportChecklist
        keys={[layerKey, companionKey]}
        ticked={{ [layerKey]: true, [companionKey]: isCompanionTicked }}
        onToggle={() => setCompanionTick(!isCompanionTicked)}
        fixedKeys={[layerKey]}
        hasData={{ [companionKey]: companionHasData }}
        lead={CHECKLIST_LEADS[area]}
        warning={!isBoundary && checklistKeys.includes("addresses") ? getAddressLimitWarning(polygon.selectedData) : null}
        onImport={isBoundary ? () => importWithinBoundary(checklistKeys) : importArea}
        onBack={() => {
          // The layer's data was confirmed for replacing before the area was chosen.
          setIsReplaceConfirmed(true);
          if (isBoundary) {
            setChosenArea(null);
          } else {
            cancelArea();
          }
        }}
      />
    );
  };

  if (task?.status === "failed") {
    return (
      <TaskProgress
        error={{
          title: `Couldn't import ${importNouns}`,
          reason: task.error ?? IMPORT_SAVE_ERROR,
          actions: [
            { label: "Cancel", onClick: dismissTask },
            { label: "Retry", onClick: retry, primary: true },
          ],
        }}
      />
    );
  }

  if (task?.status === "running") return <TaskProgress title={`Importing ${importNouns}`} phase={task.phase} />;

  if (task?.status === "succeeded") return <TaskResult message={task.result.map(({ message }) => message).join(" · ")} />;

  if (isAreaFromFile || isDrawnArea) {
    if (isAreaReady && polygon.downloadState === AsyncOperationState.failure && !polygon.status) {
      return (
        <TaskProgress
          error={{
            title: areaErrorTitle,
            reason: AREA_DOWNLOAD_ERROR,
            actions: [
              { label: "Cancel", onClick: cancelArea },
              { label: "Retry", onClick: () => dispatch(retryDownload()), primary: true },
            ],
          }}
        />
      );
    }
    if (areaWarning) {
      return (
        <TaskProgress
          error={{
            title: areaErrorTitle,
            reason: areaWarning,
            actions: [
              { label: "Cancel", onClick: cancelArea },
              ...(isAreaFromFile ? [{ label: "Choose another file", onClick: chooseAnotherFile, primary: true }] : []),
            ],
          }}
        />
      );
    }
    if (isAreaValid && companion) return renderChecklist(isAreaFromFile ? "file" : "drawn", companion);
    // An uploaded area is imported as soon as it is valid.
    if (isAreaReady) return null;
    return <PolygonFileUpload polygonSelect={polygonSelect} onUpload={uploadAreaFile} onCancel={cancelArea} />;
  }

  if (chosenArea === "boundary" && companion) return renderChecklist("boundary", companion);

  if (hasData && !isReplaceConfirmed) {
    return <ReplaceConfirmation layerNoun={noun} onCancel={onCancel} onReplace={() => setIsReplaceConfirmed(true)} />;
  }

  const hasBoundary = rows.boundary.hasData;

  return (
    <FlyoutSubOptions>
      <Typography variant="caption" color="text.secondary" px={1} pt={0.5} pb={1}>
        {`Import ${name.toLowerCase()} within…`}
      </Typography>
      <FlyoutSubOption
        icon={<CropSquare />}
        label="The boundary"
        description={hasBoundary ? "Your design area" : ADD_BOUNDARY_FIRST}
        disabled={!hasBoundary}
        onClick={importFromBoundary}
      />
      <FlyoutSubOption
        icon={<Polyline />}
        label="An area you draw"
        description="Draw a polygon on the map"
        onClick={() => open(BASE_DATA_POLYGON_DRAW_SCREEN, { purpose: "importArea", layerKey } satisfies PolygonDrawParams)}
      />
      <FlyoutSubOption
        icon={<CloudUpload />}
        label="An uploaded area"
        description="Use a polygon from a file"
        onClick={() => setChosenArea("file")}
      />
    </FlyoutSubOptions>
  );
};

/**
 * The Import {layer} option: import the layer's FOND data inside the boundary, or inside an area drawn or uploaded.
 *
 * Addresses and parcels are each offered alongside the other, in a checklist confirmed once the area is chosen.
 */
const AreaSelectSection: React.FC<AreaSelectSectionProps> = ({ layerKey, ...props }) =>
  isBoundaryImportKey(layerKey) ? <AreaImport layerKey={layerKey} {...props} /> : null;

export default AreaSelectSection;
